import { Container } from 'pixi.js';
import type { Scene } from './Scene';
import type { InputSnapshot } from '../input/actions';

/**
 * Scene stack. Menu → replace(Run) → push(LevelUpOverlay) pauses the run
 * (overlay blocksUpdateBelow) → pop → … → replace(End).
 * All scenes render every frame; update walks top-down until a blocker.
 */
export class SceneManager {
  readonly root = new Container();
  private stack: Scene[] = [];

  get top(): Scene | undefined {
    return this.stack[this.stack.length - 1];
  }

  push(scene: Scene): void {
    this.stack.push(scene);
    this.root.addChild(scene.root);
    scene.enter();
  }

  pop(): void {
    const scene = this.stack.pop();
    if (!scene) return;
    scene.exit();
    this.root.removeChild(scene.root);
  }

  replace(scene: Scene): void {
    while (this.stack.length > 0) this.pop();
    this.push(scene);
  }

  update(dt: number, input: InputSnapshot): void {
    for (let i = this.stack.length - 1; i >= 0; i--) {
      const scene = this.stack[i];
      if (!scene) continue;
      scene.update(dt, input);
      if (scene !== this.stack[i]) break; // scene replaced itself; stop this tick
      if (scene.blocksUpdateBelow) break;
    }
  }

  render(alpha: number): void {
    for (const scene of this.stack) scene.render(alpha);
  }
}
